import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  Alert,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  Autocomplete,
  FormControl,
  FormLabel,
} from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Translation from "../../utils/Translation";
import * as XLSX from 'xlsx';
import ApiService from '../../services/api/api_service';
import { ApiException } from '../../utils/exceptions';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    maxWidth: 600,
    width: "100%",
    margin: theme.spacing(2),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: "linear-gradient(135deg, #005d6d 0%, #007a8c 100%)",
  color: "white",
  padding: theme.spacing(2, 3),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  "& .MuiTypography-root": {
    fontWeight: 600,
    fontSize: "1.25rem",
  },
}));

const StepContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
  borderRadius: 12,
  border: "1px solid #e0e0e0",
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const UploadArea = styled(Box)(({ theme }) => ({
  border: "2px dashed #005d6d",
  borderRadius: 12,
  padding: theme.spacing(4),
  textAlign: "center",
  backgroundColor: "#f8fdff",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#007a8c",
    backgroundColor: "#f0faff",
  },
  "&.dragover": {
    borderColor: "#007a8c",
    backgroundColor: "#e8f7ff",
  },
}));

const PreviewContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  margin: theme.spacing(2, 0),
  borderRadius: 12,
  border: "1px solid #e0e0e0",
  backgroundColor: "#f9f9f9",
}));

const ValidationChip = styled(Chip)(({ theme, isValid }) => ({
  backgroundColor: isValid ? "#e8f5e8" : "#ffebee",
  color: isValid ? "#2e7d32" : "#c62828",
  fontWeight: 600,
  "& .MuiChip-icon": {
    color: isValid ? "#2e7d32" : "#c62828",
  },
}));

const StatsBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  "& .stat-item": {
    backgroundColor: "#005d6d",
    color: "white",
    padding: theme.spacing(1, 2),
    borderRadius: 8,
    textAlign: "center",
    minWidth: 80,
    "& .number": {
      fontSize: "1.5rem",
      fontWeight: "bold",
      display: "block",
    },
    "& .label": {
      fontSize: "0.75rem",
      opacity: 0.9,
    },
  },
}));

const steps = [
  { key: "download", icon: DownloadIcon },
  { key: "fill", icon: DescriptionIcon },
  { key: "upload", icon: UploadIcon },
];

export default function ImportProductsModal({ open, onClose, sellerInfo, auth, dynamicState }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Get addresses from Redux store
  const { data: addresses } = useSelector((state) => state.address);

  // Product import validation rules - validates first 2 products fully, rest on backend
  const validateFileStructure = (data) => {
    const results = {
      isValid: true,
      errors: [],
      warnings: [],
      totalRows: data.length - 1, // Excluding header
      headers: data.length > 0 ? data[0] : [],
      firstRow: data.length > 1 ? data[1] : [],
      productCount: 0,
      variantCount: 0,
      validatedProducts: 0,
      validatedVariants: 0,
      previewRows: [], // Rows belonging to first 2 products
    };

    const headers = results.headers;

    // Required headers based on your template
    const requiredHeaders = [
      'product_external_id',
      'sku', 
      'default_price',
      'stock'
    ];

    // Optional but recommended headers
    const recommendedHeaders = [
      'product_name',
      'product_name_ar',
      'product_description', 
      'product_description_ar',
      'product_type',
      'returnable',
      'replaceable',
      'waranty_period',
      'brand_name',      // Required only for main variant rows
      'category_name',   // Required only for main variant rows
      'tag_names',
      'main_image_url',
      'sale_price',
      'color',
      'size',
      'length_cm',       // Product dimensions - recommended but not required
      'width_cm',
      'height_cm',
      'weight_g',
      'custom_attributes',
      'detail_image_urls'
    ];

    // Check for required headers
    requiredHeaders.forEach(required => {
      if (!headers.includes(required)) {
        results.errors.push(`Missing required header: ${required}`);
        results.isValid = false;
      }
    });

    // Check for recommended headers
    recommendedHeaders.forEach(recommended => {
      if (!headers.includes(recommended)) {
        results.warnings.push(`Missing recommended header: ${recommended} (optional but recommended)`);
      }
    });

    // Check if we have data rows
    if (results.totalRows === 0) {
      results.errors.push('File contains no data rows');
      results.isValid = false;
      return results;
    }

    // Get column indices once outside the loop
    const columnIndices = {
      productExternalId: headers.indexOf('product_external_id'),
      productName: headers.indexOf('product_name'),
      productNameAr: headers.indexOf('product_name_ar'),
      productDescription: headers.indexOf('product_description'),
      productDescriptionAr: headers.indexOf('product_description_ar'),
      productType: headers.indexOf('product_type'),
      returnable: headers.indexOf('returnable'),
      replaceable: headers.indexOf('replaceable'),
      warrantyPeriod: headers.indexOf('waranty_period'),
      brandName: headers.indexOf('brand_name'),
      categoryName: headers.indexOf('category_name'),
      tagNames: headers.indexOf('tag_names'),
      mainImageUrl: headers.indexOf('main_image_url'),
      sku: headers.indexOf('sku'),
      defaultPrice: headers.indexOf('default_price'),
      salePrice: headers.indexOf('sale_price'),
      stock: headers.indexOf('stock'),
      color: headers.indexOf('color'),
      size: headers.indexOf('size'),
      lengthCm: headers.indexOf('length_cm'),
      widthCm: headers.indexOf('width_cm'),
      heightCm: headers.indexOf('height_cm'),
      weightG: headers.indexOf('weight_g'),
      customAttributes: headers.indexOf('custom_attributes'),
      detailImageUrls: headers.indexOf('detail_image_urls'),
    };

    // Debug: Log column indices
    console.log('Column indices:', columnIndices);
    console.log('Headers array:', headers);
    console.log('Dimension column indices:', {
      lengthCm: columnIndices.lengthCm,
      widthCm: columnIndices.widthCm,
      heightCm: columnIndices.heightCm,
      weightG: columnIndices.weightG
    });

    // First, identify all products and find first 2 products with their rows
    const allProductIds = new Set();
    const productRowMap = new Map(); // Map product_external_id to array of row indices
    
    // Scan all data to identify products and their rows
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.every(cell => !cell || cell.toString().trim() === '')) {
        continue; // Skip empty rows
      }

      const productId = columnIndices.productExternalId >= 0 ? row[columnIndices.productExternalId] : null;
      if (productId && productId.toString().trim() !== '') {
        allProductIds.add(productId);
        
        if (!productRowMap.has(productId)) {
          productRowMap.set(productId, []);
        }
        productRowMap.get(productId).push(i);
      }
    }

    // Get first 2 products for detailed validation
    const allProductIdArray = Array.from(allProductIds);
    const firstTwoProducts = allProductIdArray.slice(0, 2);
    const firstTwoProductRows = new Set();
    
    // Collect all rows belonging to first 2 products
    firstTwoProducts.forEach(productId => {
      const rows = productRowMap.get(productId) || [];
      rows.forEach(rowIndex => {
        firstTwoProductRows.add(rowIndex);
        results.previewRows.push(data[rowIndex]);
      });
    });

    // Analyze all data rows for basic validation and detailed validation for first 2 products
    const productIds = new Set();
    const skus = new Set();
    let rowsWithProductName = 0;
    let totalVariantRows = 0; // Count all valid variant rows
    const productVariants = new Map(); // Map to store product variants (for first 2 products only)

    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.every(cell => !cell || cell.toString().trim() === '')) {
        continue; // Skip empty rows
      }

      // Get values using proper column indices and handle undefined/empty values
      const productId = columnIndices.productExternalId >= 0 ? row[columnIndices.productExternalId] : null;
      const productName = columnIndices.productName >= 0 ? row[columnIndices.productName] : null;
      const sku = columnIndices.sku >= 0 ? row[columnIndices.sku] : null;
      const defaultPrice = columnIndices.defaultPrice >= 0 ? row[columnIndices.defaultPrice] : null;
      const stock = columnIndices.stock >= 0 ? row[columnIndices.stock] : null;

      // Check if this row belongs to first 2 products
      const isFirstTwoProducts = firstTwoProductRows.has(i);

      // Track product IDs and counts for all products
      if (productId && productId.toString().trim() !== '') {
        productIds.add(productId);
        totalVariantRows++; // Count this as a valid variant row
      }

      if (productName && productName.toString().trim() !== '') {
        rowsWithProductName++;
      }

      // Basic validation for ALL rows (required fields only)
      if (!productId || productId.toString().trim() === '') {
        results.errors.push(`Missing product_external_id in row ${i + 1}`);
        results.isValid = false;
      }

      if (!sku || sku.toString().trim() === '') {
        results.errors.push(`Missing SKU in row ${i + 1}`);
        results.isValid = false;
      }

      if (!defaultPrice || defaultPrice.toString().trim() === '') {
        results.errors.push(`Missing default_price in row ${i + 1}`);
        results.isValid = false;
      } else {
        const priceValue = parseFloat(defaultPrice);
        if (isNaN(priceValue) || priceValue < 0) {
          results.errors.push(`Invalid default_price in row ${i + 1}: must be a positive number`);
          results.isValid = false;
        }
      }

      if (stock === null || stock === undefined || stock.toString().trim() === '') {
        results.errors.push(`Missing stock in row ${i + 1}`);
        results.isValid = false;
      } else {
        const stockValue = parseInt(stock);
        if (isNaN(stockValue) || stockValue < 0) {
          results.errors.push(`Invalid stock in row ${i + 1}: must be a non-negative integer (0 or greater)`);
          results.isValid = false;
        }
      }

      // Dimension validation (warnings only - not required but recommended)
      const lengthCm = columnIndices.lengthCm >= 0 ? row[columnIndices.lengthCm] : null;
      const widthCm = columnIndices.widthCm >= 0 ? row[columnIndices.widthCm] : null;
      const heightCm = columnIndices.heightCm >= 0 ? row[columnIndices.heightCm] : null;
      const weightG = columnIndices.weightG >= 0 ? row[columnIndices.weightG] : null;

      // Check if dimensions are missing and add warnings
      if (!lengthCm || lengthCm.toString().trim() === '') {
        results.warnings.push(`Missing length_cm in row ${i + 1} - product will be imported without dimensions`);
      } else {
        const lengthValue = parseFloat(lengthCm);
        if (isNaN(lengthValue) || lengthValue <= 0) {
          results.warnings.push(`Invalid length_cm in row ${i + 1}: should be a positive number`);
        }
      }

      if (!widthCm || widthCm.toString().trim() === '') {
        results.warnings.push(`Missing width_cm in row ${i + 1} - product will be imported without dimensions`);
      } else {
        const widthValue = parseFloat(widthCm);
        if (isNaN(widthValue) || widthValue <= 0) {
          results.warnings.push(`Invalid width_cm in row ${i + 1}: should be a positive number`);
        }
      }

      if (!heightCm || heightCm.toString().trim() === '') {
        results.warnings.push(`Missing height_cm in row ${i + 1} - product will be imported without dimensions`);
      } else {
        const heightValue = parseFloat(heightCm);
        if (isNaN(heightValue) || heightValue <= 0) {
          results.warnings.push(`Invalid height_cm in row ${i + 1}: should be a positive number`);
        }
      }

      if (!weightG || weightG.toString().trim() === '') {
        results.warnings.push(`Missing weight_g in row ${i + 1} - product will be imported without dimensions`);
      } else {
        const weightValue = parseFloat(weightG);
        if (isNaN(weightValue) || weightValue <= 0) {
          results.warnings.push(`Invalid weight_g in row ${i + 1}: should be a positive number`);
        }
      }

      // DETAILED validation only for first 2 products
      if (isFirstTwoProducts) {
        const productNameAr = columnIndices.productNameAr >= 0 ? row[columnIndices.productNameAr] : null;
        const productDescription = columnIndices.productDescription >= 0 ? row[columnIndices.productDescription] : null;
        const productDescriptionAr = columnIndices.productDescriptionAr >= 0 ? row[columnIndices.productDescriptionAr] : null;
        const productType = columnIndices.productType >= 0 ? row[columnIndices.productType] : null;
        const returnable = columnIndices.returnable >= 0 ? row[columnIndices.returnable] : null;
        const replaceable = columnIndices.replaceable >= 0 ? row[columnIndices.replaceable] : null;
        const warrantyPeriod = columnIndices.warrantyPeriod >= 0 ? row[columnIndices.warrantyPeriod] : null;
        const mainImageUrl = columnIndices.mainImageUrl >= 0 ? row[columnIndices.mainImageUrl] : null;
        const salePrice = columnIndices.salePrice >= 0 ? row[columnIndices.salePrice] : null;
        const color = columnIndices.color >= 0 ? row[columnIndices.color] : null;
        const size = columnIndices.size >= 0 ? row[columnIndices.size] : null;
        const lengthCm = columnIndices.lengthCm >= 0 ? row[columnIndices.lengthCm] : null;
        const widthCm = columnIndices.widthCm >= 0 ? row[columnIndices.widthCm] : null;
        const heightCm = columnIndices.heightCm >= 0 ? row[columnIndices.heightCm] : null;
        const weightG = columnIndices.weightG >= 0 ? row[columnIndices.weightG] : null;
        const customAttributes = columnIndices.customAttributes >= 0 ? row[columnIndices.customAttributes] : null;

        // Initialize product variant tracking for first 2 products
        if (!productVariants.has(productId)) {
          productVariants.set(productId, {
            mainVariant: null,
            variants: [],
            hasColors: false,
            hasSizes: false,
            hasCustomAttributes: false,
            customAttributeKeys: new Set()
          });
        }
        
        const productData = productVariants.get(productId);
        const isMainVariant = productName && productName.toString().trim() !== '';
        
        if (isMainVariant && !productData.mainVariant) {
          productData.mainVariant = { row: i, data: row };
          
          // Check if main variant has colors/sizes/custom attributes and establish the schema
          if (color && color.toString().trim() !== '') {
            productData.hasColors = true;
          }
          if (size && size.toString().trim() !== '') {
            productData.hasSizes = true;
          }
          if (customAttributes && customAttributes.toString().trim() !== '') {
            productData.hasCustomAttributes = true;
            // Parse custom attributes to establish the required keys schema
            const attrs = customAttributes.toString().split(',');
            attrs.forEach(attr => {
              const key = attr.split(':')[0]?.trim();
              if (key) productData.customAttributeKeys.add(key);
            });
          }
        }
        
        productData.variants.push({ row: i, data: row, isMain: isMainVariant });

        // Brand name validation (required only for main variant rows with product_name)
        const brandName = columnIndices.brandName >= 0 ? row[columnIndices.brandName] : null;
        const isMainVariantRow = productName && productName.toString().trim() !== '';
        
        if (isMainVariantRow) {
          if (!brandName || brandName.toString().trim() === '') {
            results.errors.push(`Missing brand_name in main variant row ${i + 1}`);
            results.isValid = false;
          }
        }

        // Category name validation (required only for main variant rows with product_name)
        const categoryName = columnIndices.categoryName >= 0 ? row[columnIndices.categoryName] : null;
        
        if (isMainVariantRow) {
          if (!categoryName || categoryName.toString().trim() === '') {
            results.errors.push(`Missing category_name in main variant row ${i + 1}`);
            results.isValid = false;
          }
        }

        // Product type validation
        if (productType && productType.toString().trim() !== '') {
          const validTypes = ['new', 'used', 'refurbished'];
          if (!validTypes.includes(productType.toString().toLowerCase())) {
            results.errors.push(`Invalid product_type in row ${i + 1}: must be 'new', 'used', or 'refurbished'`);
            results.isValid = false;
          }
        }

        // Boolean field validation (returnable, replaceable)
        if (returnable && returnable.toString().trim() !== '') {
          const returnableStr = returnable.toString().toLowerCase();
          if (!['true', 'false', '1', '0'].includes(returnableStr)) {
            results.warnings.push(`Invalid returnable value in row ${i + 1}: should be TRUE/FALSE`);
          }
        }

        if (replaceable && replaceable.toString().trim() !== '') {
          const replaceableStr = replaceable.toString().toLowerCase();
          if (!['true', 'false', '1', '0'].includes(replaceableStr)) {
            results.warnings.push(`Invalid replaceable value in row ${i + 1}: should be TRUE/FALSE`);
          }
        }

        // Warranty period validation
        if (warrantyPeriod && warrantyPeriod.toString().trim() !== '') {
          const warrantyValue = parseInt(warrantyPeriod);
          if (isNaN(warrantyValue) || warrantyValue < 0) {
            results.warnings.push(`Invalid waranty_period in row ${i + 1}: should be a non-negative number`);
          }
        }

        // Main image URL validation
        if (mainImageUrl && mainImageUrl.toString().trim() !== '') {
          const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i;
          if (!urlPattern.test(mainImageUrl.toString())) {
            results.warnings.push(`Invalid main_image_url in row ${i + 1}: should be a valid URL`);
          }
        }

        // Sale price validation
        if (salePrice && salePrice.toString().trim() !== '') {
          const salePriceValue = parseFloat(salePrice);
          if (isNaN(salePriceValue) || salePriceValue < 0) {
            results.warnings.push(`Invalid sale_price in row ${i + 1}: should be a positive number`);
          } else if (defaultPrice && salePriceValue >= parseFloat(defaultPrice)) {
            results.warnings.push(`Sale price in row ${i + 1} should be less than default price`);
          }
        }
      }

      // SKU uniqueness validation for ALL rows
      if (sku && sku.toString().trim() !== '') {
        if (skus.has(sku)) {
          results.errors.push(`Duplicate SKU found: ${sku} (Row ${i + 1})`);
          results.isValid = false;
        }
        skus.add(sku);
      }
    }

    // Advanced validation for product variants (only for first 2 products)
    for (const [productId, productData] of productVariants) {
      if (!productData.mainVariant) {
        results.errors.push(`Product ${productId} has no main variant with product_name`);
        results.isValid = false;
        continue;
      }

      // Variant combination uniqueness validation
      const variantCombinations = new Set();
      
      for (const variant of productData.variants) {
        const variantColor = columnIndices.color >= 0 ? variant.data[columnIndices.color] : null;
        const variantSize = columnIndices.size >= 0 ? variant.data[columnIndices.size] : null;
        const variantCustomAttrs = columnIndices.customAttributes >= 0 ? variant.data[columnIndices.customAttributes] : null;
        
        // Build the combination string
        let combinationParts = [];
        
        // Add color if product has colors
        if (productData.hasColors) {
          if (!variantColor || variantColor.toString().trim() === '') {
            results.errors.push(`Product ${productId} variant in row ${variant.row + 1} missing color (required for all variants)`);
            results.isValid = false;
            continue;
          }
          combinationParts.push(`color:${variantColor.toString().trim()}`);
        }
        
        // Add size if product has sizes
        if (productData.hasSizes) {
          if (!variantSize || variantSize.toString().trim() === '') {
            results.errors.push(`Product ${productId} variant in row ${variant.row + 1} missing size (required for all variants)`);
            results.isValid = false;
            continue;
          }
          combinationParts.push(`size:${variantSize.toString().trim()}`);
        }
        
        // Add custom attributes if product has them
        if (productData.hasCustomAttributes) {
          if (!variantCustomAttrs || variantCustomAttrs.toString().trim() === '') {
            results.errors.push(`Product ${productId} variant in row ${variant.row + 1} missing custom_attributes (required for all variants)`);
            results.isValid = false;
            continue;
          }
          
          // Parse and validate custom attributes keys consistency
          const attrs = variantCustomAttrs.toString().split(',');
          const variantKeys = new Set();
          const normalizedAttrs = [];
          
          attrs.forEach(attr => {
            const [key, value] = attr.split(':');
            if (key && value) {
              const trimmedKey = key.trim();
              const trimmedValue = value.trim();
              variantKeys.add(trimmedKey);
              normalizedAttrs.push(`${trimmedKey}:${trimmedValue}`);
            }
          });
          
          // Check if variant has all required keys from main variant
          for (const requiredKey of productData.customAttributeKeys) {
            if (!variantKeys.has(requiredKey)) {
              results.errors.push(`Product ${productId} variant in row ${variant.row + 1} missing required custom attribute key '${requiredKey}'`);
              results.isValid = false;
              continue;
            }
          }
          
          // Check if variant has any extra keys not in main variant
          for (const variantKey of variantKeys) {
            if (!productData.customAttributeKeys.has(variantKey)) {
              results.errors.push(`Product ${productId} variant in row ${variant.row + 1} has unexpected custom attribute key '${variantKey}' (not in main variant)`);
              results.isValid = false;
              continue;
            }
          }
          
          // Sort attributes to ensure consistent comparison
          normalizedAttrs.sort();
          combinationParts.push(`attrs:${normalizedAttrs.join(',')}`);
        }
        
        // Create the full combination string
        const combinationKey = combinationParts.join('|');
        
        // Check for duplicate combinations
        if (variantCombinations.has(combinationKey)) {
          let readableCombo = [];
          if (productData.hasColors) readableCombo.push(`Color: ${variantColor.toString().trim()}`);
          if (productData.hasSizes) readableCombo.push(`Size: ${variantSize.toString().trim()}`);
          if (productData.hasCustomAttributes) readableCombo.push(`Attributes: ${variantCustomAttrs.toString().trim()}`);
          
          results.errors.push(`Product ${productId} has duplicate variant combination: ${readableCombo.join(', ')} (Row ${variant.row + 1})`);
          results.isValid = false;
        } else {
          variantCombinations.add(combinationKey);
        }
      }
    }

    // Set counts
    results.productCount = productIds.size;
    results.variantCount = totalVariantRows; // Use total variant rows instead of just rows with product names
    results.validatedProducts = firstTwoProducts.length;
    results.validatedVariants = results.previewRows.length;

    // Validate product structure
    if (rowsWithProductName === 0) {
      results.warnings.push('No rows contain product names. Make sure at least one variant per product has a product_name.');
    }

    if (results.productCount === 0) {
      results.errors.push('No valid products found in the file');
      results.isValid = false;
    }

    // Add summary information
    if (results.isValid) {
      if (results.productCount <= 2) {
        results.summary = `Found ${results.productCount} unique product(s) with ${results.variantCount} total variant(s). All products fully validated.`;
      } else {
        results.summary = `Found ${results.productCount} unique product(s) with ${results.variantCount} total variant(s). First ${results.validatedProducts} products (${results.validatedVariants} variants) fully validated. Remaining ${results.productCount - results.validatedProducts} products will be validated on the server.`;
      }
    }

    return results;
  };

  const readExcelFile = (file) => {
    setIsProcessing(true);
    
    // Performance optimization: Check file size and warn user
    const maxFileSize = 10 * 1024 * 1024; // 10MB limit
    if (file.size > maxFileSize) {
      setValidationResults({
        isValid: false,
        errors: [`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds the maximum limit of 10MB. Please split your file or contact support for bulk import assistance.`],
        warnings: [],
        totalRows: 0,
        headers: [],
        firstRow: [],
        productCount: 0,
        variantCount: 0,
      });
      setIsProcessing(false);
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        let jsonData;
        
        // Check if it's a CSV file
        if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
          const csvText = e.target.result;
          // Parse CSV manually or use XLSX to parse CSV
          const workbook = XLSX.read(csvText, { type: 'string' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        } else {
          // Handle Excel files with performance optimizations
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { 
            type: 'array',
            cellFormula: false, // Skip formula parsing for better performance
            cellHTML: false,    // Skip HTML parsing for better performance
            cellNF: false,      // Skip number format parsing for better performance
            cellStyles: false,  // Skip style parsing for better performance
            cellText: false,    // Skip text parsing for better performance
            cellDates: false,   // Skip date parsing for better performance
            sheetStubs: false,  // Skip stub cells for better performance
          });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          jsonData = XLSX.utils.sheet_to_json(worksheet, { 
            header: 1, 
            defval: '',
            raw: false,        // Get formatted strings for consistent processing
            dateNF: 'yyyy-mm-dd' // Standardize date format
          });
        }
        
        // Performance warning for large datasets
        if (jsonData.length > 5000) {
          console.warn(`Large file detected: ${jsonData.length} rows. Processing may take longer.`);
        }
        
        setFileData(jsonData);
        
        // Debug: Log the data structure (limit for performance)
        console.log('Raw file data rows:', jsonData.length);
        console.log('Headers:', jsonData[0]);
        console.log('First data row:', jsonData[1]);
        
        // Use setTimeout to allow UI to update before heavy validation
        setTimeout(() => {
          const validation = validateFileStructure(jsonData);
          setValidationResults(validation);
          setIsProcessing(false);
        }, 100);
        
      } catch (error) {
        console.error('Error reading file:', error);
        setValidationResults({
          isValid: false,
          errors: ['Failed to read file. Please ensure it\'s a valid Excel (.xlsx) or CSV (.csv) file. For large files, try reducing the file size or contact support.'],
          warnings: [],
          totalRows: 0,
          headers: [],
          firstRow: [],
          productCount: 0,
          variantCount: 0,
        });
        setIsProcessing(false);
      }
    };
    
    reader.onerror = () => {
      setValidationResults({
        isValid: false,
        errors: ['Failed to read file. The file may be corrupted or too large.'],
        warnings: [],
        totalRows: 0,
        headers: [],
        firstRow: [],
        productCount: 0,
        variantCount: 0,
      });
      setIsProcessing(false);
    };
    
    // Read as text for CSV, as array buffer for Excel
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && (
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "text/csv" ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.csv')
    )) {
      setSelectedFile(file);
      readExcelFile(file);
    } else {
      // Show error for invalid file type
      alert("Please select a valid Excel file (.xlsx) or CSV file (.csv)");
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file && (
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "text/csv" ||
      file.name.endsWith('.xlsx') ||
      file.name.endsWith('.csv')
    )) {
      setSelectedFile(file);
      readExcelFile(file);
    } else {
      alert("Please select a valid Excel file (.xlsx) or CSV file (.csv)");
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleUpload = async () => {
    if (selectedFile && validationResults?.isValid && fileData && selectedAddress) {
      setIsProcessing(true);
      
      try {
        // Show progress for large files
        if (validationResults.productCount > 100) {
          console.log(`Processing ${validationResults.productCount} products for upload...`);
        }
        
        // Process the file data into structured JSON (already parsed from Excel)
        const processedData = processFileDataToJSON(fileData, validationResults);
        
        // Validate processed data size for performance
        const dataSize = JSON.stringify(processedData).length;
        const maxDataSize = 5 * 1024 * 1024; // 5MB JSON limit
        
        if (dataSize > maxDataSize) {
          throw new Error(`Processed data size (${(dataSize / 1024 / 1024).toFixed(2)}MB) is too large. Please reduce the number of products or split into multiple files.`);
        }
        
        // Send structured JSON data instead of raw file
        const requestBody = {
          products: processedData.products,
          store_id: sellerInfo?.id || '',
          storage_id: selectedAddress?.fav_id, // Add the selected storage_id
          total_products: validationResults.productCount,
          total_variants: validationResults.variantCount,
          validated_products: validationResults.validatedProducts,
          validated_variants: validationResults.validatedVariants,
          upload_metadata: {
            file_name: selectedFile.name,
            file_size: selectedFile.size,
            processed_at: new Date().toISOString(),
            frontend_validation: 'first_2_products_only'
          }
        };
        
        const path = '/v1/products/import';
        const headers = {
          authorization: `Bearer ${auth.user.accessToken}`,
          contentType: 'application/json'
        };
        const options = { 
          dynamicState: dynamicState, 
          reformatData: true, // Let API service format JSON
          timeout: 120000 // 2 minute timeout for large uploads
        };
        
        const response = await ApiService.post(path, headers, requestBody, options);
        console.log('Upload successful:', response.data);
        
        // Show success message
        // BrandedSnackbarLauncher.showSuccess('Products imported successfully');
        
        // Close modal and refresh product list
        onClose();
        setSelectedFile(null);
        setFileData(null);
        setValidationResults(null);
        setSelectedAddress(null);
        
      } catch (error) {
        console.error('Upload failed:', error);
        // BrandedSnackbarLauncher.showError('Failed to import products. Please try again.');
        
        // Extract error message from ApiException
        const errorMessage = error instanceof ApiException 
          ? error.message || 'Upload failed' 
          : error.message || 'Upload failed';
          
        setValidationResults({
          ...validationResults,
          isValid: false,
          errors: [...(validationResults?.errors || []), `Upload failed: ${errorMessage}`]
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const processFileDataToJSON = (data, validation) => {
    const headers = data[0];
    const products = [];
    
    // Get column indices
    const columnIndices = {
      productExternalId: headers.indexOf('product_external_id'),
      productName: headers.indexOf('product_name'),
      productNameAr: headers.indexOf('product_name_ar'),
      productDescription: headers.indexOf('product_description'),
      productDescriptionAr: headers.indexOf('product_description_ar'),
      productType: headers.indexOf('product_type'),
      returnable: headers.indexOf('returnable'),
      replaceable: headers.indexOf('replaceable'),
      warrantyPeriod: headers.indexOf('waranty_period'),
      brandName: headers.indexOf('brand_name'),
      categoryName: headers.indexOf('category_name'),
      tagNames: headers.indexOf('tag_names'),
      mainImageUrl: headers.indexOf('main_image_url'),
      sku: headers.indexOf('sku'),
      defaultPrice: headers.indexOf('default_price'),
      salePrice: headers.indexOf('sale_price'),
      stock: headers.indexOf('stock'),
      color: headers.indexOf('color'),
      size: headers.indexOf('size'),
      lengthCm: headers.indexOf('length_cm'),
      widthCm: headers.indexOf('width_cm'),
      heightCm: headers.indexOf('height_cm'),
      weightG: headers.indexOf('weight_g'),
      customAttributes: headers.indexOf('custom_attributes'),
      detailImageUrls: headers.indexOf('detail_image_urls'),
    };

    // Group data by product_external_id
    const productGroups = new Map();
    
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      if (!row || row.every(cell => !cell || cell.toString().trim() === '')) {
        continue; // Skip empty rows
      }

      const productId = columnIndices.productExternalId >= 0 ? row[columnIndices.productExternalId] : null;
      if (!productId || productId.toString().trim() === '') continue;

      if (!productGroups.has(productId)) {
        productGroups.set(productId, []);
      }
      productGroups.get(productId).push({ row: i, data: row });
    }

    // Process each product group
    for (const [productId, variants] of productGroups) {
      // Find the main variant (the one with product_name)
      const mainVariant = variants.find(variant => {
        const productName = columnIndices.productName >= 0 ? variant.data[columnIndices.productName] : null;
        return productName && productName.toString().trim() !== '';
      });

      if (!mainVariant) continue; // Skip if no main variant found

      const mainRow = mainVariant.data;

      // Extract main product data from the main variant
      const product = {
        product_external_id: productId,
        product_name: columnIndices.productName >= 0 ? mainRow[columnIndices.productName] : '',
        product_name_ar: columnIndices.productNameAr >= 0 ? mainRow[columnIndices.productNameAr] : '',
        product_description: columnIndices.productDescription >= 0 ? mainRow[columnIndices.productDescription] : '',
        product_description_ar: columnIndices.productDescriptionAr >= 0 ? mainRow[columnIndices.productDescriptionAr] : '',
        product_type: columnIndices.productType >= 0 ? mainRow[columnIndices.productType] : '',
        returnable: columnIndices.returnable >= 0 ? parseBooleanValue(mainRow[columnIndices.returnable]) : null,
        replaceable: columnIndices.replaceable >= 0 ? parseBooleanValue(mainRow[columnIndices.replaceable]) : null,
        warranty_period: columnIndices.warrantyPeriod >= 0 ? parseInt(mainRow[columnIndices.warrantyPeriod]) || 0 : 0,
        brand_name: columnIndices.brandName >= 0 ? mainRow[columnIndices.brandName] : '',
        category_name: columnIndices.categoryName >= 0 ? mainRow[columnIndices.categoryName] : '',
        tag_names: columnIndices.tagNames >= 0 ? parseTagNames(mainRow[columnIndices.tagNames]) : [],
        main_image_url: columnIndices.mainImageUrl >= 0 ? mainRow[columnIndices.mainImageUrl] : '',
        details: [] // Array of variants
      };

      // Process all variants for this product
      variants.forEach(variant => {
        const variantRow = variant.data;
        
        const variantDetail = {
          sku: columnIndices.sku >= 0 ? variantRow[columnIndices.sku] : '',
          default_price: columnIndices.defaultPrice >= 0 ? parseFloat(variantRow[columnIndices.defaultPrice]) || 0 : 0,
          sale_price: columnIndices.salePrice >= 0 ? parseFloat(variantRow[columnIndices.salePrice]) || null : null,
          stock: columnIndices.stock >= 0 ? parseInt(variantRow[columnIndices.stock]) || 0 : 0,
          color: columnIndices.color >= 0 ? variantRow[columnIndices.color] : '',
          size: columnIndices.size >= 0 ? variantRow[columnIndices.size] : '',
          // Handle dimensions gracefully - allow null/undefined values
          length_cm: columnIndices.lengthCm >= 0 && variantRow[columnIndices.lengthCm] !== null && variantRow[columnIndices.lengthCm] !== undefined && variantRow[columnIndices.lengthCm] !== '' ? parseFloat(variantRow[columnIndices.lengthCm]) : null,
          width_cm: columnIndices.widthCm >= 0 && variantRow[columnIndices.widthCm] !== null && variantRow[columnIndices.widthCm] !== undefined && variantRow[columnIndices.widthCm] !== '' ? parseFloat(variantRow[columnIndices.widthCm]) : null,
          height_cm: columnIndices.heightCm >= 0 && variantRow[columnIndices.heightCm] !== null && variantRow[columnIndices.heightCm] !== undefined && variantRow[columnIndices.heightCm] !== '' ? parseFloat(variantRow[columnIndices.heightCm]) : null,
          weight_g: columnIndices.weightG >= 0 && variantRow[columnIndices.weightG] !== null && variantRow[columnIndices.weightG] !== undefined && variantRow[columnIndices.weightG] !== '' ? parseFloat(variantRow[columnIndices.weightG]) : null,
          custom_attributes: columnIndices.customAttributes >= 0 ? parseCustomAttributes(variantRow[columnIndices.customAttributes]) : {},
          detail_image_urls: columnIndices.detailImageUrls >= 0 ? parseImageUrls(variantRow[columnIndices.detailImageUrls]) : []
        };

        product.details.push(variantDetail);
      });

      products.push(product);
    }

    return { products };
  };

  // Helper function to parse boolean values
  const parseBooleanValue = (value) => {
    if (!value || value.toString().trim() === '') return null;
    const str = value.toString().toLowerCase();
    if (['true', '1'].includes(str)) return true;
    if (['false', '0'].includes(str)) return false;
    return null;
  };

  // Helper function to parse tag names
  const parseTagNames = (value) => {
    if (!value || value.toString().trim() === '') return [];
    return value.toString().split(',').map(tag => tag.trim()).filter(tag => tag !== '');
  };

  // Helper function to parse custom attributes
  const parseCustomAttributes = (value) => {
    if (!value || value.toString().trim() === '') return {};
    const attributes = {};
    const pairs = value.toString().split(',');
    
    pairs.forEach(pair => {
      const [key, val] = pair.split(':');
      if (key && val) {
        attributes[key.trim()] = val.trim();
      }
    });
    
    return attributes;
  };

  // Helper function to parse image URLs
  const parseImageUrls = (value) => {
    if (!value || value.toString().trim() === '') return [];
    return value.toString().split(',').map(url => url.trim()).filter(url => url !== '');
  };

  const handleClose = () => {
    onClose();
    setSelectedFile(null);
    setIsDragOver(false);
    setFileData(null);
    setValidationResults(null);
    setIsProcessing(false);
    setSelectedAddress(null);
  };

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <StyledDialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <UploadIcon />
          <Translation data="store.import_products" />
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ color: "white" }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent sx={{ padding: 3 }}>
        <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
          <Translation data="store.import_description" />
        </Typography>

        <Stepper alternativeLabel sx={{ mb: 4 }}>
          {steps.map((step, index) => (
            <Step key={step.key}>
              <StepLabel
                StepIconComponent={() => {
                  const IconComponent = step.icon;
                  return (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "#005d6d",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "white",
                      }}
                    >
                      <IconComponent />
                    </Box>
                  );
                }}
              >
                <Translation data={`store.import_step_${index + 1}`} />
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack spacing={3}>
          <StepContainer elevation={0}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <DownloadIcon sx={{ color: "#005d6d" }} />
              <Typography variant="h6" fontWeight={600}>
                <Translation data="store.step_1_title" />
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              <Translation data="store.step_1_description" />
            </Typography>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              sx={{
                borderColor: "#005d6d",
                color: "#005d6d",
                "&:hover": {
                  borderColor: "#007a8c",
                  backgroundColor: "#f8fdff",
                },
              }}
            >
              <Link
                href="import-products-example.csv"
                target="_blank"
                rel="noopener"
                sx={{ textDecoration: "none", color: "inherit" }}
              >
                <Translation data="store.download_template" />
              </Link>
            </Button>
          </StepContainer>

          <StepContainer elevation={0}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <DescriptionIcon sx={{ color: "#005d6d" }} />
              <Typography variant="h6" fontWeight={600}>
                <Translation data="store.step_2_title" />
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              <Translation data="store.step_2_description" />
            </Typography>
          </StepContainer>

          <StepContainer elevation={0}>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <UploadIcon sx={{ color: "#005d6d" }} />
              <Typography variant="h6" fontWeight={600}>
                <Translation data="store.step_3_title" />
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" mb={2}>
              <Translation data="store.step_3_description" />
            </Typography>

            {/* Address Selection */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: "#005d6d" }}>
                <Translation data="store_seller.select_storage_address" />
              </FormLabel>
              <Autocomplete
                value={selectedAddress}
                onChange={(_, newValue) => setSelectedAddress(newValue)}
                options={addresses || []}
                getOptionLabel={(option) => option?.address_data?.friendly_name || ""}
                isOptionEqualToValue={(option, value) => option?.fav_id === value?.fav_id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select storage address..."
                    variant="outlined"
                    error={!selectedAddress}
                    helperText={!selectedAddress ? "Please select a storage address for your products" : ""}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {option?.address_data?.friendly_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option?.address_data?.address}
                      </Typography>
                    </Box>
                  </li>
                )}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </FormControl>
            
            <UploadArea
              className={isDragOver ? "dragover" : ""}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => document.getElementById("file-input").click()}
            >
              <input
                id="file-input"
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
              
              {selectedFile ? (
                <Box>
                  <CheckCircleIcon sx={{ fontSize: 48, color: "#4caf50", mb: 1 }} />
                  <Typography variant="h6" color="#4caf50" gutterBottom>
                    <Translation data="store.file_selected" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <UploadIcon sx={{ fontSize: 48, color: "#005d6d", mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    <Translation data="store.drag_drop_file" />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <Translation data="store.or_click_to_browse" />
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                    <Translation data="store.supported_formats" />
                  </Typography>
                </Box>
              )}
            </UploadArea>

            {selectedFile && (
              <Alert
                severity="success"
                sx={{ mt: 2 }}
                icon={<CheckCircleIcon />}
              >
                <Translation data="store.ready_to_upload" />
              </Alert>
            )}
          </StepContainer>

          {/* File Preview Section */}
          {validationResults && (
            <PreviewContainer elevation={0}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <VisibilityIcon sx={{ color: "#005d6d" }} />
                <Typography variant="h6" fontWeight={600}>
                  <Translation data="store.file_preview" />
                </Typography>
              </Box>

              {/* Validation Status */}
              <Box mb={3}>
                <ValidationChip
                  icon={validationResults.isValid ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={validationResults.isValid ? "Valid Format" : "Invalid Format"}
                  isValid={validationResults.isValid}
                />
                
                {validationResults.errors.length > 0 && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translation data="store.validation_errors" />:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {validationResults.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                {validationResults.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      <Translation data="store.validation_warnings" />:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {validationResults.warnings.map((warning, index) => (
                        <li key={index}>{warning}</li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Box>

              {/* Statistics */}
              <StatsBox>
                <Box className="stat-item">
                  <span className="number">{validationResults.productCount}</span>
                  <span className="label">
                    <Translation data="store.unique_products" />
                  </span>
                </Box>
                <Box className="stat-item">
                  <span className="number">{validationResults.variantCount}</span>
                  <span className="label">
                    <Translation data="store.total_variants" />
                  </span>
                </Box>
                <Box className="stat-item" sx={{ backgroundColor: "#4caf50 !important" }}>
                  <span className="number">{validationResults.validatedProducts}</span>
                  <span className="label">
                    Validated Products
                  </span>
                </Box>
                <Box className="stat-item" sx={{ backgroundColor: "#4caf50 !important" }}>
                  <span className="number">{validationResults.validatedVariants}</span>
                  <span className="label">
                    Validated Variants
                  </span>
                </Box>
              </StatsBox>

              {/* Summary */}
              {validationResults.summary && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {validationResults.summary}
                  {validationResults.productCount > 2 && (
                    <Box sx={{ mt: 1, fontSize: '0.875rem', color: '#1976d2' }}>
                      ℹ️ Note: Only the first 2 products are fully validated here. All remaining products will undergo complete validation on the server during upload.
                    </Box>
                  )}
                  {/* Performance guidance */}
                  {selectedFile && selectedFile.size > 5 * 1024 * 1024 && (
                    <Box sx={{ mt: 1, fontSize: '0.875rem', color: '#ff9800' }}>
                      ⚠️ Large file detected. Processing may take 10-30 seconds. Please be patient during upload.
                    </Box>
                  )}
                </Alert>
              )}

              {/* Header and First Row Preview */}
              {validationResults.headers.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    <Translation data="store.data_preview" />:
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {validationResults.headers.map((header, index) => (
                            <TableCell key={index} sx={{ fontWeight: 600, backgroundColor: "#f5f5f5" }}>
                              {header || `Column ${index + 1}`}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResults.previewRows && validationResults.previewRows.length > 0 ? (
                          <>
                            {/* Show all rows from first 2 products */}
                            {validationResults.previewRows.map((row, rowIndex) => (
                              <TableRow key={rowIndex}>
                                {validationResults.headers.map((header, cellIndex) => (
                                  <TableCell key={cellIndex}>
                                    {row[cellIndex] !== null && row[cellIndex] !== undefined ? String(row[cellIndex]) : ''}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            {validationResults.totalRows > validationResults.previewRows.length && (
                              <TableRow>
                                <TableCell colSpan={validationResults.headers.length} sx={{ 
                                  textAlign: 'center', 
                                  fontStyle: 'italic', 
                                  color: '#666',
                                  backgroundColor: '#f0f0f0'
                                }}>
                                  <Typography variant="body2" sx={{ py: 1 }}>
                                    ✓ First {validationResults.validatedProducts} products ({validationResults.validatedVariants} variants) shown above - fully validated<br/>
                                    📊 Remaining {validationResults.productCount - validationResults.validatedProducts} products ({validationResults.totalRows - validationResults.validatedVariants} variants) will be validated on the server
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : (
                          <>
                            {/* Fallback to show first few rows if previewRows is empty */}
                            {fileData.slice(1, 5).map((row, rowIndex) => (
                              <TableRow key={rowIndex}>
                                {validationResults.headers.map((header, cellIndex) => (
                                  <TableCell key={cellIndex}>
                                    {row[cellIndex] !== null && row[cellIndex] !== undefined ? String(row[cellIndex]) : ''}
                                  </TableCell>
                                ))}
                              </TableRow>
                            ))}
                            {validationResults.totalRows > 4 && (
                              <TableRow>
                                <TableCell colSpan={validationResults.headers.length} sx={{ 
                                  textAlign: 'center', 
                                  fontStyle: 'italic', 
                                  color: '#666' 
                                }}>
                                  <Translation data="store.more_rows_message" /> ({validationResults.totalRows - 4} <Translation data="store.more_rows" />)
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </PreviewContainer>
          )}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ padding: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#666",
            "&:hover": {
              borderColor: "#999",
            },
          }}
        >
          <Translation data="home.cancel" />
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!selectedFile || !validationResults?.isValid || !selectedAddress || isProcessing}
          sx={{
            backgroundColor: "#005d6d",
            "&:hover": {
              backgroundColor: "#007a8c",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {isProcessing ? (
            validationResults?.productCount > 100 
              ? `Processing ${validationResults.productCount} products...` 
              : 'Processing...'
          ) : (
            <Translation data="store.upload_products" />
          )}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}